import { batch } from 'editor/src/store/batchedSubscribeEnhancer';
import { updateTextStyleAction } from 'editor/src/store/design/slice';
import { TextStyle } from 'editor/src/store/design/types';
import { getTextStyle, loadTextStyleColorProfile } from 'editor/src/store/design/util/textStyles';
import saveUndoRedoStateOperation from 'editor/src/store/editorModules/undoRedo/operation/saveUndoRedoStateOperation';
import type { ThunkDispatch } from 'editor/src/store/hooks';
import type { RootState } from 'editor/src/store/index';

import applyTextStyleToMembersOperation from './applyTextStyleToMembersOperation';
import loadTextStyleFontOperation from './loadTextStyleFontOperation';

// The font each style is waiting for, so that when the author picks fonts faster
// than they load only the last pick is applied. Kept per store (thunk getState is the
// store's own), so two editors or two test stores never see each other's picks.
const pendingFontsByStore = new WeakMap<() => RootState, { [styleId: string]: string }>();
function getPendingFonts(getState: () => RootState) {
  let pending = pendingFontsByStore.get(getState);
  if (!pending) {
    pending = {};
    pendingFontsByStore.set(getState, pending);
  }
  return pending;
}

/**
 * `failed`: the style's font could not be loaded, so nothing changed and the
 * caller should tell the author. `skipped`: nothing to do (unknown style, or a
 * later font pick superseded this one).
 */
export type TextStyleUpdateResult = 'applied' | 'failed' | 'skipped';

export interface UpdateTextStyleOptions {
  /** False inside an edit session (textStyleEditSession), whose one undo step was saved when it opened. */
  saveUndo?: boolean;
  /**
   * Checked once the style's font has loaded: false drops the edit. An edit session that was
   * cancelled while a font was still loading must not land afterwards.
   */
  isCurrent?: () => boolean;
}

/**
 * Edits a style and re-lays out every text that follows it, as one undo step.
 * A font change waits for the font to load first, so the texts are measured
 * in the font they will render in.
 */
const updateTextStyleOperation =
  (
    styleId: string,
    update: Partial<Omit<TextStyle, 'id'>>,
    { saveUndo = true, isCurrent }: UpdateTextStyleOptions = {},
  ) =>
  async (dispatch: ThunkDispatch, getState: () => RootState): Promise<TextStyleUpdateResult> => {
    const style = getTextStyle(getState().design.designData, styleId);
    if (!style) {
      return 'skipped';
    }

    // Clearing an optional property (colour, line height, alignment...) leaves each member's own
    // value alone, so only setting one changes how the members look.
    const changesLook =
      'fontFamily' in update ||
      'fontSize' in update ||
      (
        [
          'color',
          'lineHeight',
          'textAlign',
          'underline',
          'linethrough',
          'letterSpacing',
          'paragraphSpacing',
          'sectionSpacing',
        ] as const
      ).some((key) => update[key] !== undefined);
    const pickedFont = update.fontFamily !== undefined;
    const pendingFonts = getPendingFonts(getState);
    if (pickedFont) {
      // Every font pick, even back to the current one, supersedes a pick still loading.
      pendingFonts[styleId] = update.fontFamily!;
    }
    if (changesLook) {
      // Every look edit re-measures the members, so the font they render in must be
      // loaded first — including on a size edit, when members on spreads never shown
      // have not loaded it yet.
      const loaded = await dispatch(loadTextStyleFontOperation(update.fontFamily ?? style.fontFamily));
      if (getState().hostSettings.cmykOnly) {
        await loadTextStyleColorProfile();
      }
      if (pickedFont) {
        if (pendingFonts[styleId] !== update.fontFamily) {
          return 'skipped';
        }
        delete pendingFonts[styleId];
      }
      if (isCurrent && !isCurrent()) {
        return 'skipped';
      }
      if (!loaded) {
        return 'failed';
      }
      if (!getTextStyle(getState().design.designData, styleId)) {
        return 'skipped';
      }
    }

    batch(() => {
      if (saveUndo) {
        dispatch(saveUndoRedoStateOperation('Text style updated'));
      }
      dispatch(updateTextStyleAction({ id: styleId, update }));
      if (changesLook) {
        dispatch(applyTextStyleToMembersOperation(styleId));
      }
    });
    return 'applied';
  };

export default updateTextStyleOperation;
